import { FC, useState, Suspense, useRef } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Toolbar } from 'primereact/toolbar';
import { Skill } from '../../models/Skill';
import { SkillsByType } from '../../models/SingleUnit';
import SingleUnitSupportSkillsContainer from './SingleUnitSupportSkillsContainer';
import SingleUnitDataTable from './SingleUnitDataTable';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { Button } from 'primereact/button';
import { UsersBySkillResults } from '../../models/UsersBySkillResults';
import { progressBarSingleUnit } from '../../businessLogicService/progressBarAssignValues';
import SingleUnitProgressBar from './SingleUnitProgressBar';
import { ProgressBarParent } from '../../models/ProgressBar';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import SingleUnitSkillDialog from './SingleUnitSkillDialog';
import { TieredMenu } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import { SelectAssignmentModeModal } from '../../models/Assignment';
import { AssignmentSidebarOpenModal } from '../AssignmentSidebar/AssignemntSidebarOpenModal/AssignmentSidebarOpenModal';
import { AssignmentOpenModalButton } from '../Assignments/AssignmentOpenModalButton';
import { PresentLessonButton } from '../TeacherLedLesson/PresentLessonButton';

interface Props {
  skillsInUnit?: SkillsByType[];
  keyProp: number;
  targetSkillsIds: string[];
}

const SingleUnitRightColumn: FC<Props> = ({ skillsInUnit, keyProp, targetSkillsIds }) => {
  const tieredMenuRef = useRef<TieredMenu>(null);
  const [itemsTieredMenu, setItemsTieredMenu] = useState<MenuItem[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<Skill>();
  const [showCreateAssignmentModel, setCreateAssignmentModal] = useState<SelectAssignmentModeModal>({
    visible: false,
    skill: null,
  });
  const skillsTypeTarget = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.TARGET);
  const skillsTypeSupport = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.SUPPORT);
  const skillsTypeExtension = skillsInUnit?.find(el => el.targetSkillType === TargetSkillTypeLabel.EXTENSION);
  const firstLessonSkillId = skillsTypeTarget?.skills.find(s => s.lessons?.length)?.id;

  const accordionStart = (skillValue: Skill) => {
    return (
      <span className="flex align-items-center gap-md">
        <i className="nc_icon-nc_target icon-sm" />
        <h3>
          {skillValue.name} ({skillValue.code})
        </h3>
        {skillValue.lessonNumbers?.length ? (
          <h6 className="nc_unit-lessons">Lessons: {skillValue.lessonNumbers.map(el => el).join(', ')}</h6>
        ) : (
          <></>
        )}
      </span>
    );
  };

  const accordionProgressBar = (usersBySkillResults: UsersBySkillResults[]) => {
    const values = progressBarSingleUnit(usersBySkillResults);
    return (
      <SingleUnitProgressBar
        values={values}
        parent={ProgressBarParent.SINGLE_UNIT}
        tieredMenuRef={tieredMenuRef}
        setItemsTieredMenu={setItemsTieredMenu}
      />
    );
  };

  const accordionEnd = (skillValue: Skill) => {
    return (
      <div className="tw-flex tw-items-center">
        <PresentLessonButton
          skill={{ id: skillValue.id, code: skillValue.code, name: skillValue.name }}
          disabled={!skillValue.lessons?.length}
          showCoachmark={skillValue.id === firstLessonSkillId}
        />
        <Button
          className="p-button-link"
          label="View"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setSkill(skillValue);
            setVisible(true);
          }}
        ></Button>
        <AssignmentOpenModalButton
          setCreateAssignmentModal={setCreateAssignmentModal}
          entryPoint="units"
          skill={{
            id: skillValue.id,
            code: skillValue.code,
            name: skillValue.name,
            targetSkillType: skillValue.targetSkillType,
          }}
        />
      </div>
    );
  };

  const accordionHeader = (skill: Skill) => {
    return (
      <Toolbar
        start={() => accordionStart(skill)}
        center={
          skill.usersBySkillResults && (() => accordionProgressBar(skill.usersBySkillResults as UsersBySkillResults[]))
        }
        end={() => accordionEnd(skill)}
        className="w-full nc_accordion-header nc_unit-skill"
      />
    );
  };

  const supportAndExtensionCenter = (type: TargetSkillTypeLabel) => {
    const name = type === TargetSkillTypeLabel.SUPPORT ? 'Additional Support' : 'Extension';
    return (
      <span className="flex gap-md">
        <i className={`nc_icon-nc_${type.toLowerCase()} icon-sm`} />
        <h3>{name} Skills</h3>
      </span>
    );
  };

  const supportAndExtensionHeader = (type: TargetSkillTypeLabel) => {
    return (
      <Toolbar center={() => supportAndExtensionCenter(type)} className="w-full nc_accordion-header" role="section" />
    );
  };

  return (
    <>
      <AssignmentSidebarOpenModal
        showCreateAssignmentModel={showCreateAssignmentModel}
        setCreateAssignmentModal={setCreateAssignmentModal}
      />
      {skillsInUnit && (
        <Suspense fallback={<HorizontalLoading />}>
          <Tooltip target=".nc_icon-nc_target.icon-sm">Target Skill</Tooltip>
          <Tooltip target=".nc_icon-nc_support.icon-sm">Support Skill</Tooltip>
          <Tooltip target=".nc_icon-nc_extension.icon-sm">Extension Skill</Tooltip>

          <Accordion className="flex flex-column gap-sm justify-content-start" key={keyProp}>
            {skillsTypeTarget?.skills.map(skill => {
              return (
                <AccordionTab
                  key={skill.id}
                  header={() => accordionHeader(skill)}
                  className="nc_unit-skill-accordion nc_unit-target-skill"
                >
                  <div className="nc_content-frame flex flex-column gap-lg pt-0">
                    {skill.skillAncestries && (
                      <SingleUnitSupportSkillsContainer
                        skillTypeUnit={skillsTypeTarget.skills}
                        skillAncestries={skill.skillAncestries}
                        tieredMenuRef={tieredMenuRef}
                        setItemsTieredMenu={setItemsTieredMenu}
                        targetSkillsIds={targetSkillsIds}
                      />
                    )}
                  </div>
                </AccordionTab>
              );
            })}

            <AccordionTab
              header={() => supportAndExtensionHeader(TargetSkillTypeLabel.SUPPORT)}
              className="nc_unit-skill-accordion nc_unit-skill-accordion-padded nc_unit-additional-support-skill"
            >
              <SingleUnitDataTable
                skills={skillsTypeSupport?.skills as Skill[]}
                tieredMenuRef={tieredMenuRef}
                setItemsTieredMenu={setItemsTieredMenu}
              />
            </AccordionTab>

            <AccordionTab
              header={() => supportAndExtensionHeader(TargetSkillTypeLabel.EXTENSION)}
              className="nc_unit-skill-accordion nc_unit-skill-accordion-padded nc_unit-extension-skill"
            >
              <SingleUnitDataTable
                skills={skillsTypeExtension?.skills as Skill[]}
                tieredMenuRef={tieredMenuRef}
                setItemsTieredMenu={setItemsTieredMenu}
              />
            </AccordionTab>
          </Accordion>
        </Suspense>
      )}
      {visible && skill && <SingleUnitSkillDialog visible={visible} setVisible={setVisible} skill={skill} />}
      {itemsTieredMenu && tieredMenuRef && (
        <TieredMenu
          appendTo={document.body}
          model={itemsTieredMenu}
          popup
          ref={tieredMenuRef}
          breakpoint="767px"
          className="nc_tieredmenu-fixed-height"
        />
      )}
    </>
  );
};

export default SingleUnitRightColumn;
